/**
 * German labels and the booking state machine, keyed on the GENERATED enums.
 *
 * The maps are typed as `Record<components['schemas']['X'], string>`, so a state
 * added to the backend enum is a type error here rather than a blank cell on a
 * screen. That is not pedantry: the scaffold this replaces carried its own copy
 * of three enums and three of the values had drifted apart from the backend
 * (`planned` for `scheduled`, `change_requested` for `changes_requested`,
 * `unpublished` for `depublished`): every one of them would have rendered as
 * undefined against real data.
 *
 * The transition table itself stays in the frontend on purpose. It is a
 * description of concept 9.7.2 for a person reading the help page, not tenant
 * data, and an endpoint that served a constant would be a round trip for a
 * document.
 */

import type { components } from "@countdown/api-client";
import { lastMomentBefore } from "@countdown/theme";

type ActionStatus = components["schemas"]["ActionStatus"];
type BookingState = components["schemas"]["BookingWorkflowState"];
type ConsentType = components["schemas"]["ConsentType"];
type PrizeStatus = components["schemas"]["PrizeAwardStatus"];
type SponsorStatus = components["schemas"]["SponsorStatus"];
type SuppressionReason = components["schemas"]["SuppressionReason"];

export type { ActionStatus, BookingState, PrizeStatus, SponsorStatus };

/** Why an address receives no mail (concept 9.6.3). */
export const SUPPRESSION_REASON_LABELS: Readonly<
  Record<SuppressionReason, string>
> = {
  unsubscribe: "Abgemeldet",
  hard_bounce: "Unzustellbar",
  blocked: "Gesperrt",
  unconfirmed: "Nicht bestätigt",
};

/** The four separate consents (concept 3), in the words of the publishing warnings. */
export const CONSENT_TYPE_LABELS: Readonly<Record<ConsentType, string>> = {
  registration: "Teilnahme",
  newsletter: "Newsletter",
  sponsor_contact: "Sponsorenkontakt",
  daily_reminder: "Tagesmail",
};

/**
 * What a placeholder of a consent wording stands for, as the server fills it in
 * (`services/consents.py`). The chips under a wording field name it.
 */
export const PLACEHOLDER_LABELS: Readonly<Record<string, string>> = {
  action: "Titel der Aktion",
  controller: "Veranstalter",
  controller_address: "Anschrift des Veranstalters",
  fields: "Angaben, die übermittelt werden",
  privacy_policy_url: "Link zur Datenschutzerklärung",
  sponsor: "Name des Sponsors",
};

export const ACTION_STATUS_LABELS: Readonly<Record<ActionStatus, string>> = {
  draft: "Entwurf",
  scheduled: "Geplant",
  live: "Live",
  paused: "Pausiert",
  finished: "Beendet",
  archived: "Archiviert",
};

/** Whether an action still counts (02.11.2026): an archived one is gone from every list but
 *  the archive, until the superadmin brings it back. */
export function notArchived(action: { readonly status: ActionStatus }): boolean {
  return action.status !== "archived";
}

export const SPONSOR_STATUS_LABELS: Readonly<Record<SponsorStatus, string>> = {
  prospect: "Interessent",
  active: "Aktiv",
  inactive: "Inaktiv",
};

/** What a sponsor may change in the configurator (E6), in the order both forms list it. */
export const SPONSOR_RIGHT_LABELS = {
  slide_structure: "Folien hinzufügen, entfernen und umstellen",
  quiz: "Quiz bearbeiten",
  videos: "Videos auf Folien",
  link_buttons: "Links nach außen",
  prize: "Gewinn bearbeiten und nachbessern",
} as const satisfies Record<
  Exclude<
    keyof components["schemas"]["SponsorRights"],
    "sponsor_edits" | "pictures" | "max_slides" | "self_publish" | "embed"
  >,
  string
>;

export type SponsorRight = keyof typeof SPONSOR_RIGHT_LABELS;

/**
 * The built-in roles of the backoffice (`models.enums.Permission`, D42). The session sends the
 * operator's client roles under `roles`; what they open it sends under `elements` (D57).
 */
export const PERMISSION_LABELS = {
  doors: "Türchen-Inhalte",
  approval: "Freigabe",
  sponsors: "Sponsoren",
  design: "Gestaltung",
  publish: "Schalten",
  prizes: "Gewinne",
  privacy: "Datenschutz",
  settings: "Stammdaten",
  emergency: "Störfall",
  superadmin: "Superadmin",
} as const;

export type Permission = keyof typeof PERMISSION_LABELS;

/** One form, section or button the superadmin grants to roles (`models.enums.Element`, D57). */
export type BackofficeElement = components["schemas"]["Element"];

/** The forms of the tab »Einstellungen«; the tab shows when one of them is granted. */
export const SETTINGS_TAB_ELEMENTS: readonly BackofficeElement[] = [
  "action_basics",
  "action_organizer",
  "action_embed",
  "action_notifications",
  "action_identity",
  "action_terms",
  "sponsor_workflow",
];

/** The forms of the tab »Gestaltung«. */
export const DESIGN_TAB_ELEMENTS: readonly BackofficeElement[] = [
  "calendar_layout",
  "calendar_style",
  "door_number_style",
  "action_fallback",
  "action_embed",
  "door_window",
  "lead_fields",
  "account_offer",
];

/**
 * Whether the session's elements include one of `elements`. A courtesy for the screen; the
 * server decides.
 */
export function can(granted: readonly string[], ...elements: readonly BackofficeElement[]): boolean {
  return elements.some((element) => granted.includes(element));
}

/**
 * `audit_log.event_type` values. Free text in the database, so the screen shows the raw key
 * for anything missing here rather than a blank cell.
 */
export const AUDIT_EVENT_LABELS: Readonly<Record<string, string>> = {
  booking_invited: "Sponsor eingeladen",
  booking_content_saved: "Inhalt gespeichert",
  booking_submitted: "Beitrag eingereicht",
  booking_review_started: "Prüfung begonnen",
  booking_changes_requested: "Änderung angefordert",
  booking_approved: "Beitrag freigegeben",
  booking_approval_withdrawn: "Freigabe zurückgenommen",
  booking_went_live: "Türchen live",
  booking_finished: "Belegung abgeschlossen",
  booking_not_delivered: "Nicht geliefert",
  booking_prize_improved: "Gewinn nachgebessert",
  booking_correction_discarded: "Korrektur verworfen",
  booking_sent_to_sponsor: "Zur Freigabe an den Sponsor gesendet",
  booking_sponsor_approved: "Vom Sponsor freigegeben",
  booking_sponsor_changes_requested: "Änderung vom Sponsor gewünscht",
  booking_published_without_sponsor: "Ohne Freigabe des Sponsors veröffentlicht",
  booking_sponsor_reminded: "Sponsor an Freigabe erinnert",
  booking_released: "Belegung aufgehoben",
  booking_image_uploaded: "Bild hochgeladen",
  booking_image_published: "Bild veröffentlicht",
  sponsor_logo_uploaded: "Sponsorlogo hochgeladen",
  sponsor_logo_published: "Sponsorlogo veröffentlicht",
  brand_logo_uploaded: "Markenlogo hochgeladen",
  placeholder_image_uploaded: "Bild für unbelegte Türchen hochgeladen",
  fallback_image_uploaded: "Bild des Ersatzinhalts hochgeladen",
  example_media_uploaded: "Bild der Beispielansicht hochgeladen",
  sponsor_invitation_link_shown: "Einladungslink angezeigt",
  sponsor_account_bound: "Konto mit Sponsor verbunden",
  sponsor_account_revoked: "Verbindung zum Sponsorenportal gelöst",
  sponsor_access_invited: "Weitere Person zum Sponsorenportal eingeladen",
  sponsor_signed_in: "Im Sponsorenportal angemeldet",
  sponsor_created: "Sponsor angelegt",
  sponsor_saved: "Stammdaten geändert",
  sponsor_status_changed: "Zustand geändert",
  booking_placed: "Auf ein Türchen gesetzt",
  transfer_agreement_accepted: "Übermittlungsvereinbarung angenommen",
  "emergency.participant_blocked": "Not-Aus: Teilnehmer gesperrt",
  "emergency.participant_unblocked": "Not-Aus: Sperre aufgehoben",
  "emergency.field_depublished": "Not-Aus: Türchen depubliziert",
  "emergency.field_republished": "Not-Aus: Türchen wieder online",
  "emergency.action_paused": "Not-Aus: Kalender geschlossen",
  "emergency.action_resumed": "Not-Aus: Kalender wieder geöffnet",
  "emergency.dispatch_stopped": "Not-Aus: Versand gestoppt",
  "emergency.dispatch_resumed": "Not-Aus: Versand wieder aufgenommen",
  prize_address_read: "Gewinneranschrift abgerufen",
  prize_shipped: "Gewinn versendet",
  prize_completed: "Gewinnübergabe abgeschlossen",
  // The steps the prize dialog reads from the timestamps (D95), not from the log.
  prize_drawn: "Gewinner gezogen",
  prize_substitute_drawn: "Nachrücker gezogen",
  prize_confirmed: "Gewinn angenommen",
  prize_declined: "Auf den Gewinn verzichtet",
  prize_forfeited: "Gewinn verfallen",
  prize_redrawn: "Neu gezogen",
  participant_record_viewed: "Teilnehmerbestand eingesehen",
  participant_erased: "Teilnehmer gelöscht",
  participant_form_erased: "Formular eines Teilnehmers geleert",
  test_data_purged: "Testdaten entfernt",
  participant_email_confirmation_imported: "E-Mail-Bestätigung übernommen",
  retention_run: "Löschlauf",
  suppressions_reapplied: "Sperrliste neu angewendet",
  configuration_changed: "Einstellung geändert",
  action_published: "Aktion live geschaltet",
  action_finished: "Aktion beendet",
  action_archived: "Aktion archiviert",
  action_reactivated: "Aktion reaktiviert",
  action_test_flag_changed: "Testmarkierung geändert",
  action_rebranded: "Aktion einer anderen Marke zugeordnet",
};

/** `audit_log.object_type` values; the raw key is the fallback here too. */
export const AUDIT_OBJECT_LABELS: Readonly<Record<string, string>> = {
  action: "Aktion",
  field: "Türchen",
  field_booking: "Belegung",
  sponsor: "Sponsor",
  brand: "Marke",
  consent_template: "Einwilligungstext",
  participant: "Teilnehmer",
  prize_award: "Gewinn",
  cell: "Gesamtbestand",
  element_grants: "Rechte",
};

/**
 * `config.RetentionCategory`, the keys of a cell's deletion periods (docs/loeschmatrix.md).
 * Plain strings in the API (a dict key), so a category missing here shows its raw key.
 */
export const RETENTION_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  participation_drafts: "Teilnahme-Entwürfe",
  participations: "Teilnahmen",
  leads: "Leads",
  consent_records: "Einwilligungsnachweise",
  winner_data: "Gewinnerdaten",
  access_logs: "Zugriffsprotokolle",
  draw_logs: "Ziehungsprotokolle",
  sponsor_contacts: "Sponsorenkontakte",
};

type CellPolicy = components["schemas"]["CellPolicyView"];

/** The keys of the tenant settings, short, as the tenant menu and the protocol name them. */
export const TENANT_SETTING_LABELS: Readonly<Record<keyof CellPolicy, string>> = {
  calendar_list: "Kalenderliste",
  house_font: "Hausschrift",
  newsletter: "Newsletter",
  participant_sign_in: "Anmelden für Teilnehmende",
  sender_addresses: "Absender",
  login_issuers: "Anmeldedienste",
  mail_footer: "Fußzeile der E-Mails",
  retention_days: "Löschfristen",
};

/** Where an empty list or no own period falls back to. */
export const PLATFORM_RULE = "Plattformregel";

/** The tenant settings as label and value, in the menu's order. */
export function tenantSettingLines(policy: CellPolicy): [string, string][] {
  const periods = Object.entries(policy.retention_days).map(
    ([category, days]) =>
      `${RETENTION_CATEGORY_LABELS[category] ?? category}: ${String(days)} Tage`,
  );
  const list = (values: readonly string[]): string =>
    values.length === 0 ? PLATFORM_RULE : values.join(", ");
  const onOff = (value: boolean): string => (value ? "an" : "aus");
  return [
    [TENANT_SETTING_LABELS.sender_addresses, list(policy.sender_addresses)],
    [TENANT_SETTING_LABELS.login_issuers, list(policy.login_issuers)],
    [TENANT_SETTING_LABELS.retention_days, list(periods)],
    // Without lines the mails name the magazine alone (D94).
    [
      TENANT_SETTING_LABELS.mail_footer,
      policy.mail_footer.length === 0 ? "Name der Zeitschrift" : policy.mail_footer.join(" · "),
    ],
    [TENANT_SETTING_LABELS.calendar_list, onOff(policy.calendar_list)],
    [TENANT_SETTING_LABELS.house_font, onOff(policy.house_font)],
    [TENANT_SETTING_LABELS.newsletter, onOff(policy.newsletter)],
    [TENANT_SETTING_LABELS.participant_sign_in, onOff(policy.participant_sign_in)],
  ];
}

/** `admin_access_log.access_kind`: what the platform operation did. */
export const PLATFORM_ACCESS_KIND_LABELS: Readonly<
  Record<components["schemas"]["AdminAccessKind"], string>
> = {
  read: "Lesen",
  write: "Ändern",
  export: "Export",
  support_session: "Support-Anmeldung",
};

/** `admin_access_log.object_reference` up to its first colon; see `platformReferenceLabel`. */
const PLATFORM_REFERENCE_LABELS: Readonly<Record<string, string>> = {
  backoffice_sign_in: "Anmeldung im Backoffice",
  cell_create: "Mandant eingerichtet",
  cell_delete: "Mandant gelöscht",
  cell_migrate: "Datenbank aktualisiert",
  cell_verify: "Datenbank geprüft",
  cell_seed: "Testdaten angelegt",
  domain_add: "Domain eingetragen",
  identity_realm: "Anmeldedienst festgelegt",
  identity_realm_sync: "Anmeldedienst abgeglichen",
  policy: "Einstellungen geändert",
  suppressions_export: "Sperrliste exportiert",
  suppressions_apply: "Sperrliste übernommen",
};

/**
 * One reference in German: the known prefix translated, its detail kept (a host, the changed
 * settings by name). A reference this list does not know is shown as it comes.
 */
export function platformReferenceLabel(reference: string | null): string {
  if (reference === null) {
    return "";
  }
  const at = reference.indexOf(":");
  const key = at === -1 ? reference : reference.slice(0, at);
  const detail = at === -1 ? "" : reference.slice(at + 1);
  const label = PLATFORM_REFERENCE_LABELS[key];
  if (label === undefined) {
    return reference;
  }
  const shown =
    key === "policy"
      ? detail
          .split(",")
          .filter((name) => name !== "")
          .map((name) => (TENANT_SETTING_LABELS as Record<string, string>)[name] ?? name)
          .join(", ")
      : detail;
  return shown === "" ? label : `${label}: ${shown}`;
}

/** Image scan states that block a release (`AssetScanStatus` without `clean`). */
export { SCAN_STATUS_LABELS } from "@countdown/door-editor";

/** Same wording as `prize_cli.py`'s table, so a handover started in one place reads the same
 *  in the other. */
export const PRIZE_STATUS_LABELS: Readonly<Record<PrizeStatus, string>> = {
  drawn: "Gezogen",
  notified: "Benachrichtigt",
  confirmed: "Anschrift liegt vor",
  shipped: "Versendet",
  completed: "Abgeschlossen",
  forfeited: "Verfallen",
};

export interface BookingWorkflowStep {
  readonly state: BookingState;
  readonly label: string;
  readonly trigger: string;
  readonly effect: string;
  readonly kind: "main" | "special";
  /** States this one can move to: `ALLOWED_TRANSITIONS` in `services/bookings.py`, verbatim. */
  readonly next: readonly BookingState[];
}

/** The state machine of one booking (concept 9.7.2, the workflow of G7). */
export const BOOKING_WORKFLOW: readonly BookingWorkflowStep[] = [
  {
    state: "invited",
    label: "Eingeladen",
    trigger:
      "Belegung angelegt; die Einladung geht ab dem Livegang automatisch raus. Bis eine Einladung an die Firma versendet ist, steht im Türchenplan »Offen«",
    effect:
      "Nach der Einladung kann der Sponsor das Portal öffnen; noch kein Inhalt vorhanden",
    kind: "main",
    next: ["draft"],
  },
  {
    state: "draft",
    label: "Entwurf",
    trigger: "Der Sponsor bearbeitet Inhalt, Frage und Gewinn",
    effect: "Änderbar; nichts davon ist für Teilnehmer sichtbar",
    kind: "main",
    next: ["submitted"],
  },
  {
    state: "submitted",
    label: "Eingereicht",
    trigger: "Der Sponsor gibt den Beitrag ab",
    effect: "Inhalt für den Sponsor eingefroren, er steht für diesen Stand ein; im Backoffice weiter änderbar",
    kind: "main",
    next: ["in_review"],
  },
  {
    state: "in_review",
    label: "In Prüfung",
    trigger: "Der Beitrag kommt im Backoffice in die Prüfung",
    effect: "Für den Sponsor weiterhin eingefroren; Freigabe oder Rückgabe steht aus",
    kind: "main",
    next: ["approved", "changes_requested"],
  },
  {
    state: "changes_requested",
    label: "Änderung angefordert",
    trigger: "Rückgabe mit Pflichtkommentar",
    effect: "Der Sponsor kann wieder bearbeiten; die Abgabefrist läuft weiter",
    kind: "main",
    next: ["draft", "submitted"],
  },
  {
    state: "approved",
    label: "Freigegeben",
    trigger: "Freigabe im Backoffice",
    effect:
      "Der Beitrag geht am Anzeigetag online; ein Bild wird öffentlich gestellt",
    kind: "main",
    next: ["live", "draft"],
  },
  {
    state: "live",
    label: "Live",
    trigger: "Das Türchen ist am Kalendertag geöffnet",
    effect: "Teilnehmer sehen den Beitrag und können antworten",
    kind: "main",
    next: ["finished"],
  },
  {
    state: "finished",
    label: "Abgeschlossen",
    trigger: "Nachlauffrist abgelaufen",
    effect: "Keine Teilnahme mehr möglich; Auswertung steht",
    kind: "main",
    next: [],
  },
  {
    state: "depublished",
    label: "Depubliziert",
    trigger: "Not-Aus für dieses Türchen",
    effect: "Belegung angehalten; das Türchen zeigt den Ersatzinhalt",
    kind: "special",
    next: [],
  },
];

export const BOOKING_WORKFLOW_BY_STATE: Readonly<
  Record<BookingState, BookingWorkflowStep>
> = Object.fromEntries(
  BOOKING_WORKFLOW.map((step) => [step.state, step]),
) as Record<BookingState, BookingWorkflowStep>;

export const NUMBER_FORMAT = new Intl.NumberFormat("de-DE");
export const DATE_FORMAT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
export const DATE_TIME_FORMAT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** ISO date (`2026-12-01`) to `01.12.2026`. */
export function formatDate(value: string): string {
  return DATE_FORMAT.format(new Date(value));
}

export function formatDateTime(value: string): string {
  return DATE_TIME_FORMAT.format(new Date(value));
}

/** The daily deadline: the API sends its exclusive end (midnight), people read 23:59. */
export function formatDeadline(value: string): string {
  return formatDateTime(lastMomentBefore(value));
}

const WEEKDAY_DATE_FORMAT = new Intl.DateTimeFormat("de-DE", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const TIME_FORMAT = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });

/** When a door opens, for the plan: `Mo., 07.12.2026`, the time only when it is not midnight. */
export function formatOpening(value: string): string {
  const date = new Date(value);
  const day = WEEKDAY_DATE_FORMAT.format(date);
  return date.getHours() === 0 && date.getMinutes() === 0
    ? day
    : `${day}, ${TIME_FORMAT.format(date)}`;
}

/**
 * The daily deadline next to the opening: `bis 23:59` when it falls on the same day, which is
 * nearly always; the day as well when it does not.
 */
export function formatDeadlineAfter(opensAt: string, deadline: string): string {
  const start = new Date(opensAt);
  const end = new Date(lastMomentBefore(deadline));
  const time = TIME_FORMAT.format(end);
  return start.toDateString() === end.toDateString()
    ? `bis ${time}`
    : `bis ${WEEKDAY_DATE_FORMAT.format(end)}, ${time}`;
}

/** A short code fit for a chip: what a tenant types as a magazine key, `pt` or `sub`. */
const SHORT_CODE = /^[a-z0-9]{1,6}$/;

/**
 * The chip of an action in the sponsor list: the magazine's short code in capitals where the
 * tenant keeps one, else the start of the title. Not every tenant has short codes, and a key
 * like `brand_3f2a` is no word for a person.
 */
export function actionChipLabel(action: { readonly title: string; readonly brand_key: string }): string {
  if (SHORT_CODE.test(action.brand_key)) {
    return action.brand_key.toUpperCase();
  }
  return action.title.length > 16 ? `${action.title.slice(0, 15)}…` : action.title;
}
